// Administration → Étages & services: floors (order, color, name, description, context, studio —
// on, every department of the floor has one —, the chicken and the cat of its 3D view, « Données de
// l’étage » — the Tables du bureau, notes and team of another floor, its host —, allowed
// connectors — the ones added in the office,
// the library's, are on every floor: listed apart, never in the allowlist), their departments
// (color, context, lead, « Changer d’étage » for the whole department)
// and agents ("Nouvel agent", link to the agent's settings), with create / edit / delete (only
// when empty) and up/down reordering (floors, departments, and the agents of a department: the
// order of their desks on the office plan, the lead staying first), the structure export / import
// (web/ui/admin/structure.js) and the reseed.
// Endpoints (SPEC §13): POST /api/floors · PATCH|DELETE /api/floors/:id ·
// POST /api/floors/:id/departments · PATCH|DELETE /api/departments/:id ·
// PUT /api/departments/:id/agent-order · POST /api/departments/:id/move · POST /api/agents.
import { h, button, iconButton, input, select, field, badge, emptyState, statusDot, icon, switchToggle, infoTip } from '../../dom.js';
import { t } from '../../i18n/fr.js';
import { sortedFloors, floorDepartments, deptAgents } from '../../store.js';
import { floorNumber } from '../../scene/layout.js';
import { deptColor, pad2, connectorLabel, initials, hashHue } from '../../format.js';
import { iconForConnector } from '../connectoricons.js';
import { isLibraryKey } from '../connectorpolicy.js';
import {
  validateFloor, validateDept, validateAgent, changedFields, reorderPatches, agentOrderAfterMove, withAgentOrder,
  floorCounts, deptLead, officeWith, dataHostOf, sameDataFloors, dataGuestsOf, dataFloorChoices, moveTargets, moveSummary,
  officeAfterMove, unwrapItem, sortServers, connectorTone, errorText, serverEngineLabel, COLORS, LIMITS, AGENT_ENGINES,
} from './model.js';
import { sectionHead, countedTextarea, colorPicker, formError, setBusy, focusKeeper, aid } from './common.js';
import { openExport, openImport } from './structure.js';
import './strings.js';

const enc = encodeURIComponent;

/** First palette color not used by `items` (floors or departments), else the first one. */
function freeColor(items) {
  const used = new Set((items || []).map((x) => x.color));
  return COLORS.find((c) => !used.has(c)) || COLORS[0];
}

/**
 * Creates the floors & departments tab.
 * @param {object} ctx app context
 * @returns {{el: HTMLElement, reload(): Promise<void>, destroy(): void}}
 */
export function createFloorsView(ctx) {
  const office = () => ctx.store.get().office;
  const expanded = new Set();
  const keeper = focusKeeper();
  let servers = null;          // GET /api/connectors servers, null while unknown/unavailable
  let serversFailed = false;
  let moving = false;
  let destroyed = false;

  const newBtn = button(t('adm.floors.new'), { variant: 'primary', icon: 'plus', onClick: () => openFloorForm(null) });
  const reseedBtn = button(t('adm.reseed.button'), { variant: 'ghost', size: 'sm', icon: 'refresh', class: 'adm-reseed', title: t('adm.reseed.buttonTitle'), onClick: () => openReseed() });
  const exportBtn = button(t('adm.structure.exportButton'), { variant: 'ghost', size: 'sm', icon: 'download', class: 'adm-structure-export', title: t('adm.structure.exportButtonTitle'), onClick: () => openExport(ctx, openForm) });
  const importBtn = button(t('adm.structure.importButton'), { variant: 'ghost', size: 'sm', icon: 'upload', class: 'adm-structure-import', title: t('adm.structure.importButtonTitle'), onClick: () => openImport(ctx, openForm, afterChange) });
  const head = sectionHead({ title: t('adm.floors.title'), intro: t('adm.floors.intro'), actions: [exportBtn, importBtn, reseedBtn, newBtn] });
  const body = h('div', { class: 'adm-body adm-floor-list' });
  const el = h('section', { class: 'adm-view adm-floors' }, head, body);

  async function loadConnectors() {
    try {
      const res = await ctx.api.get('/api/connectors');
      if (destroyed) return;
      servers = Array.isArray(res) ? res : Array.isArray(res?.servers) ? res.servers : [];
      serversFailed = false;
    } catch {
      if (destroyed) return;
      serversFailed = true;
    }
    render();
  }

  function serverName(key) {
    const s = (servers || ctx.store.get().connectors || []).find((x) => x.key === key);
    return s?.library ? String(s.label || s.name || key) : connectorLabel(s?.name || key);
  }

  /** The library's connectors (added in the office): on every floor, without any allowlist. */
  function libraryServers() {
    return (servers || ctx.store.get().connectors || []).filter((x) => x?.library === true);
  }

  function libraryNames() {
    return libraryServers().map((x) => String(x.label || x.name || x.key));
  }

  function setOffice(next) {
    ctx.store.set({ office: next });
  }

  function afterChange() {
    ctx.refresh?.office?.();
  }

  // --- rendering --------------------------------------------------------------------------------
  function render() {
    const restore = keeper.begin();
    const floors = sortedFloors(office().floors);
    head.setCount(t('adm.floors.count', { n: floors.length }));
    if (!floors.length) {
      body.replaceChildren(emptyState({
        icon: 'building', title: t('adm.floors.empty.title'), text: t('adm.floors.empty.text'),
        action: button(t('adm.floors.new'), { variant: 'primary', icon: 'plus', onClick: () => openFloorForm(null) }),
      }));
      restore();
      return;
    }
    body.replaceChildren(...floors.map((f, i) => floorCard(f, i, floors.length)));
    restore();
  }

  /**
   * Up / down buttons of a floor, a department or an agent. `first` is the highest place the item
   * may reach (1 for an agent when its department's lead holds the first place).
   */
  function moveButtons(kind, item, idx, count, scopeId, { first = 0, onMove = (delta) => move(kind, item.id, delta, scopeId) } = {}) {
    return [
      keeper.reg(`${kind}-up:${item.id}`, iconButton('chevronUp', t('adm.moveUp', { name: item.name }), {
        variant: 'ghost', size: 16, disabled: idx <= first, onClick: () => onMove(-1),
      })),
      keeper.reg(`${kind}-down:${item.id}`, iconButton('chevronDown', t('adm.moveDown', { name: item.name }), {
        variant: 'ghost', size: 16, disabled: idx === count - 1, onClick: () => onMove(1),
      })),
    ];
  }

  function floorCard(f, idx, count) {
    const o = office();
    const depts = floorDepartments(o.departments, f.id);
    const counts = floorCounts(o, f.id);
    const allowed = Array.isArray(f.connectors) ? f.connectors : [];
    const del = keeper.reg(`floor-del:${f.id}`, button(t('common.delete'), {
      variant: 'ghost', size: 'sm', class: 'adm-del', ariaLabel: t('adm.floor.delete', { name: f.name }),
      onClick: () => deleteFloor(f),
    }));
    if (depts.length) { del.setAttribute('aria-disabled', 'true'); del.title = t('adm.floor.deleteBlocked'); }
    // Données de l’étage: a guest names its host; a host, the floors that use its data.
    const hostId = dataHostOf(o.floors, f.id);
    const host = hostId !== f.id ? o.floors.find((x) => x.id === hostId) : null;
    const guests = host ? [] : dataGuestsOf(o.floors, f.id);
    return h('article', { class: 'adm-floor', style: { '--floor': deptColor(f.color, f.id) }, dataset: { id: f.id } },
      h('header', { class: 'adm-floor-head' },
        h('span', { class: 'adm-floor-num', title: t('adm.floor.num', { n: floorNumber(f, idx) }) }, pad2(floorNumber(f, idx))),
        h('div', { class: 'adm-floor-text' },
          h('h4', { class: 'adm-floor-name' }, h('span', { class: 'adm-floor-swatch', 'aria-hidden': 'true' }), f.name,
            f.studio === true ? badge(t('adm.floor.studioBadge'), 'info', { class: 'adm-floor-studio', title: t('adm.floor.studioBadgeTitle') }) : null,
            host ? badge(t('adm.floor.dataBadge', { name: host.name }), 'neutral', { class: 'adm-floor-data', title: t('adm.floor.dataBadgeTitle', { name: host.name }) }) : null),
          f.description ? h('p', { class: 'adm-floor-desc' }, f.description) : null,
          h('p', { class: 'adm-floor-meta mono-label' },
            [t('adm.floor.depts', { n: counts.depts }), t('adm.floor.agents', { n: counts.agents })].join(' · ')),
          guests.length
            ? h('p', { class: 'adm-floor-conns adm-floor-shares' }, t('adm.floor.dataShares', { list: guests.map((g) => g.name).join(', ') }))
            : null,
          h('p', { class: 'adm-floor-conns' }, allowed.length
            ? t('adm.floor.connectors', { list: allowed.map(serverName).join(', ') })
            : h('span', { class: 'muted' }, t('adm.floor.noConnectors'))),
          libraryNames().length
            ? h('p', { class: 'adm-floor-conns adm-floor-library muted' }, t('adm.floor.libraryAll', { list: libraryNames().join(', ') }))
            : null),
        h('div', { class: 'adm-floor-actions' },
          moveButtons('floor', f, idx, count, null),
          keeper.reg(`floor-edit:${f.id}`, button(t('adm.floor.edit'), {
            variant: 'secondary', size: 'sm', ariaLabel: t('adm.floor.editAria', { name: f.name }), onClick: () => openFloorForm(f),
          })),
          del)),
      depts.length
        ? h('ul', { class: 'adm-depts' }, depts.map((d, i) => deptItem(f, d, i, depts.length)))
        : h('p', { class: 'adm-depts-empty muted' }, t('adm.floor.noDepts')),
      h('footer', { class: 'adm-floor-foot' },
        keeper.reg(`dept-new:${f.id}`, button(t('adm.floor.newDept'), { variant: 'ghost', size: 'sm', icon: 'plus', onClick: () => openDeptForm(f, null) }))));
  }

  function deptItem(f, d, idx, count) {
    const o = office();
    const agents = deptAgents(o.agents, d.id);
    const lead = deptLead(o, d);
    const open = expanded.has(d.id);
    const panelId = `adm-agents-${d.id}`;
    const toggle = keeper.reg(`dept-toggle:${d.id}`, h('button', {
      type: 'button', class: 'adm-dept-toggle', 'aria-expanded': String(open), 'aria-controls': panelId,
      onClick: () => { if (expanded.has(d.id)) expanded.delete(d.id); else expanded.add(d.id); render(); },
    }, h('span', { class: 'mono-label' }, t('adm.dept.showAgents', { n: agents.length })), icon(open ? 'chevronUp' : 'chevronDown', { size: 14 })));
    const del = keeper.reg(`dept-del:${d.id}`, button(t('common.delete'), {
      variant: 'ghost', size: 'sm', class: 'adm-del', ariaLabel: t('adm.dept.delete', { name: d.name }), onClick: () => deleteDept(d),
    }));
    if (agents.length) { del.setAttribute('aria-disabled', 'true'); del.title = t('adm.dept.deleteBlocked'); }
    return h('li', { class: 'adm-dept', style: { '--dept': deptColor(d.color, d.id) }, dataset: { id: d.id } },
      h('div', { class: 'adm-dept-row' },
        h('span', { class: 'adm-dept-dot', 'aria-hidden': 'true' }),
        h('div', { class: 'adm-dept-text' },
          h('span', { class: 'adm-dept-name' }, d.name),
          h('span', { class: 'adm-dept-lead' }, lead ? t('adm.dept.lead', { name: lead.name }) : h('span', { class: 'muted' }, t('adm.dept.noLead')))),
        toggle,
        h('div', { class: 'adm-dept-actions' },
          moveButtons('dept', d, idx, count, f.id),
          keeper.reg(`dept-edit:${d.id}`, button(t('common.edit'), {
            variant: 'ghost', size: 'sm', ariaLabel: t('adm.dept.edit', { name: d.name }), onClick: () => openDeptForm(f, d),
          })),
          // The whole department to another floor (only when there is one).
          o.floors.length > 1 ? keeper.reg(`dept-move:${d.id}`, button(t('adm.dept.move'), {
            variant: 'ghost', size: 'sm', class: 'adm-dept-move', ariaLabel: t('adm.dept.moveAria', { name: d.name }), onClick: () => openMoveDept(f, d),
          })) : null,
          del)),
      h('div', { class: 'adm-agents', id: panelId, hidden: !open },
        agents.length > 1 ? h('p', { class: 'adm-note adm-agents-order' }, t('adm.agent.orderHint')) : null,
        agents.length
          ? h('ul', { class: 'adm-agent-list' }, agents.map((a, i) => agentItem(a, d, i, agents)))
          : h('p', { class: 'muted adm-agents-empty' }, t('adm.agent.none')),
        keeper.reg(`agent-new:${d.id}`, button(t('adm.agent.new'), { variant: 'secondary', size: 'sm', icon: 'plus', onClick: () => openAgentForm(f, d) }))));
  }

  function agentItem(a, d, idx, agents) {
    const isLead = a.id === d.leadAgentId || (!d.leadAgentId && a.lead);
    // The lead keeps the first place (its desk faces the team on the plan): no arrows, a spacer.
    const first = agents[0]?.lead ? 1 : 0;
    const moves = agents.length < 2 ? null
      : a.lead ? h('span', { class: 'adm-agent-moves is-lead', title: t('adm.agent.leadFirst') })
        : h('span', { class: 'adm-agent-moves' }, moveButtons('agent', a, idx, agents.length, d.id, { first, onMove: (delta) => moveAgent(d, a.id, delta) }));
    return h('li', { class: 'adm-agent', dataset: { id: a.id } },
      moves,
      h('span', { class: 'adm-agent-mono', 'aria-hidden': 'true', style: { '--hue': String(hashHue(a.id)) } }, initials(a.name)),
      h('span', { class: 'adm-agent-text' },
        h('span', { class: 'adm-agent-name' }, a.name, isLead ? h('span', { class: 'adm-agent-lead', title: t('agent.lead') }, ' ★') : null),
        h('span', { class: 'adm-agent-role' }, a.role || '')),
      // The engine behind the agent (C6 names: Claude, Codex (ChatGPT), Images OpenAI, Gemini).
      a.engine ? badge(AGENT_ENGINES.includes(a.engine) ? t(`engine.name.${a.engine}`) : String(a.engine), 'info', { class: 'adm-engine-badge', title: t('adm.agent.engine') }) : null,
      keeper.reg(`agent-settings:${a.id}`, button(t('adm.agent.settings'), {
        variant: 'ghost', size: 'sm', iconRight: 'arrowRight', ariaLabel: t('adm.agent.settingsAria', { name: a.name }),
        onClick: () => ctx.navigate(`#/agent/${enc(a.id)}/settings`),
      })));
  }

  // --- reordering ---------------------------------------------------------------------------------
  async function move(kind, id, delta, floorId) {
    if (moving) return;
    const o = office();
    const items = kind === 'floor' ? o.floors : floorDepartments(o.departments, floorId);
    const patches = reorderPatches(items, id, delta);
    if (!patches.length) return;
    const listKey = kind === 'floor' ? 'floors' : 'departments';
    const orders = new Map(patches.map((p) => [p.id, p.order]));
    setOffice({ ...o, [listKey]: o[listKey].map((x) => (orders.has(x.id) ? { ...x, order: orders.get(x.id) } : x)) });
    moving = true;
    try {
      for (const p of patches) {
        await ctx.api.patch(`/api/${kind === 'floor' ? 'floors' : 'departments'}/${enc(p.id)}`, { order: p.order });
      }
    } catch (err) {
      ctx.toast.error(err);
    } finally {
      moving = false;
      afterChange();
    }
  }

  /** Moves an agent within its department: the whole order goes to PUT /api/departments/:id/agent-order. */
  async function moveAgent(d, id, delta) {
    if (moving) return;
    const o = office();
    const ids = agentOrderAfterMove(deptAgents(o.agents, d.id), id, delta);
    if (!ids) return;
    setOffice({ ...o, agents: withAgentOrder(o.agents, d.id, ids) });
    moving = true;
    try {
      await ctx.api.put(`/api/departments/${enc(d.id)}/agent-order`, { agentIds: ids });
    } catch (err) {
      ctx.toast.error(err);
    } finally {
      moving = false;
      afterChange();
    }
  }

  // --- deletes ----------------------------------------------------------------------------------------
  async function deleteFloor(f) {
    if (floorDepartments(office().departments, f.id).length) { ctx.toast.warn(t('adm.floor.deleteBlocked')); return; }
    const ok = await ctx.overlay.confirm({
      title: t('adm.floor.deleteTitle', { name: f.name }), message: t('adm.floor.deleteText'),
      confirmLabel: t('common.delete'), danger: true,
    });
    if (!ok) return;
    try {
      await ctx.api.del(`/api/floors/${enc(f.id)}`);
      setOffice(officeWith(office(), 'floors', f, { remove: true }));
      ctx.toast.success(t('adm.floor.deleted', { name: f.name }));
      newBtn.focus?.();
      afterChange();
    } catch (err) {
      ctx.toast.error(err);
    }
  }

  async function deleteDept(d) {
    if (deptAgents(office().agents, d.id).length) { ctx.toast.warn(t('adm.dept.deleteBlocked')); return; }
    const ok = await ctx.overlay.confirm({
      title: t('adm.dept.deleteTitle', { name: d.name }), message: t('adm.dept.deleteText'),
      confirmLabel: t('common.delete'), danger: true,
    });
    if (!ok) return;
    try {
      await ctx.api.del(`/api/departments/${enc(d.id)}`);
      setOffice(officeWith(office(), 'departments', d, { remove: true }));
      ctx.toast.success(t('adm.dept.deleted', { name: d.name }));
      afterChange();
    } catch (err) {
      ctx.toast.error(err);
    }
  }

  // --- forms --------------------------------------------------------------------------------------------
  /** Shared modal form scaffolding: returns {layer, submitBtn, err}; `danger` makes the submit red. */
  function openForm({ title, subtitle, submitLabel, fields, onSubmit, danger = false }) {
    const err = formError();
    const formId = aid('form');
    let layer = null;
    const submitBtn = button(submitLabel || t('common.save'), { variant: danger ? 'danger' : 'primary', type: 'submit' });
    submitBtn.setAttribute('form', formId);
    const cancel = button(t('common.cancel'), { variant: 'ghost', onClick: () => layer?.close('cancel') });
    const form = h('form', {
      id: formId, class: 'stack adm-form', novalidate: true,
      onSubmit: async (e) => {
        e?.preventDefault?.();
        if (submitBtn.disabled) return;
        err.set(null);
        setBusy(submitBtn, true);
        try {
          const closeIt = await onSubmit({ err });
          if (closeIt) layer?.close('api'); else setBusy(submitBtn, false);
        } catch (ex) {
          setBusy(submitBtn, false);
          err.set(errorText(ex));
        }
      },
    }, fields, err);
    layer = ctx.overlay.openModal({ title, subtitle, size: 'md', content: form, footer: [cancel, submitBtn], className: 'adm-modal' });
    return { layer, form, submitBtn, err };
  }

  /** Checkbox list of the probed connectors (+ keys that are no longer detected). */
  function connectorChecklist(selected) {
    const chosen = new Set(selected || []);
    const legendId = aid('conns');
    const box = h('fieldset', { class: 'adm-fieldset field', 'aria-describedby': `${legendId}-hint` },
      h('legend', { class: 'field-label', id: legendId }, t('adm.floor.allowed')),
      h('p', { class: 'field-hint', id: `${legendId}-hint` }, t('adm.floor.allowedHint')));
    if (!servers) {
      box.appendChild(h('p', { class: 'adm-note' }, serversFailed ? t('adm.floor.allowedUnavailable') : t('adm.loading')));
      return { el: box, getValue: () => [...chosen] };
    }
    // The library's connectors are on every floor: shown apart, never ticked nor sent (the server
    // drops them from an allowlist).
    const library = sortServers(servers.filter((s) => s.library === true));
    const known = sortServers(servers.filter((s) => s.library !== true));
    const unknown = [...chosen].filter((k) => !servers.some((s) => s.key === k) && !isLibraryKey(k));
    const libraryBox = library.length
      ? h('div', { class: 'adm-floor-libbox' },
        h('p', { class: 'field-hint adm-floor-libhint' }, t('adm.floor.libraryHint')),
        h('ul', { class: 'adm-checklist adm-floor-liblist' }, library.map((s) => {
          const name = String(s.label || s.name || s.key);
          return h('li', { class: 'adm-check is-library', dataset: { key: s.key } },
            iconForConnector({ key: s.catalogId || s.key, name }, { size: 26 }),
            h('span', { class: 'adm-check-text' }, name,
              h('span', { class: 'adm-check-meta mono-label' },
                [s.addedByName ? t('connector.addedBy', { name: s.addedByName }) : '', t(`connector.status.${['connected', 'needs-auth', 'added', 'error'].includes(s.status) ? s.status : 'unknown'}`)]
                  .filter(Boolean).join(' · '))),
            badge(t('adm.floor.everyFloor'), 'info'));
        })))
      : null;
    if (!known.length && !unknown.length) {
      box.appendChild(h('p', { class: 'adm-note' }, t('adm.floor.allowedNone')));
      if (libraryBox) box.appendChild(libraryBox);
      return { el: box, getValue: () => [] };
    }
    const inputs = [];
    const list = h('div', { class: 'adm-checklist' });
    for (const s of known) {
      const cb = h('input', { type: 'checkbox', class: 'adm-check-input', value: s.key, checked: chosen.has(s.key) });
      inputs.push(cb);
      const label = connectorLabel(s.name || s.key);
      list.appendChild(h('label', { class: 'adm-check' }, cb,
        iconForConnector(s, { size: 26 }),
        h('span', { class: 'adm-check-text' }, label,
          h('span', { class: 'adm-check-meta mono-label' }, serverEngineLabel(s), ' · ',
            t(`connector.status.${s.status || 'unknown'}`))),
        statusDot(connectorTone(s.status))));
    }
    for (const key of unknown) {
      const cb = h('input', { type: 'checkbox', class: 'adm-check-input', value: key, checked: true });
      inputs.push(cb);
      list.appendChild(h('label', { class: 'adm-check is-unknown' }, cb,
        h('span', { class: 'adm-check-text' }, key, h('span', { class: 'adm-check-meta mono-label' }, t('adm.floor.allowedUnknown')))));
    }
    box.appendChild(list);
    if (libraryBox) box.appendChild(libraryBox);
    return { el: box, getValue: () => inputs.filter((i) => i.checked).map((i) => i.value) };
  }

  function openFloorForm(f) {
    const isNew = !f;
    const nameInput = input({ name: 'name', value: f?.name || '', maxLength: LIMITS.floorName, placeholder: t('adm.floor.namePlaceholder'), autofocus: true, autocomplete: 'off' });
    const fName = field(t('adm.floor.name'), nameInput);
    const descInput = input({ name: 'description', value: f?.description || '', maxLength: LIMITS.floorDescription, autocomplete: 'off' });
    const fDesc = field(t('adm.floor.description'), descInput, { hint: t('adm.floor.descriptionHint'), optional: true });
    const color = colorPicker({ value: f?.color || freeColor(office().floors), label: t('adm.floor.color') });
    const ctxArea = countedTextarea({ value: f?.context || '', max: LIMITS.floorContext, rows: 8, name: 'context' });
    const fCtx = field(t('adm.floor.context'), ctxArea, { hint: t('adm.floor.contextHint'), optional: true });
    // The floor's studio, for every one of its services (the server keeps it on while it holds elements).
    const studio = switchToggle(t('adm.floor.studio'), { checked: f?.studio === true, hint: t('adm.floor.studioHint') });
    // « La poule de l’étage »: decoration drawn by the browser (web/scene), nothing on the server.
    // Its « ? » stays out of the switch's label, where a tap on the bubble would flip the switch.
    const chicken = switchToggle(t('adm.floor.chicken'), { checked: f?.chicken === true });
    const fChicken = h('div', { class: 'adm-switch-tip' }, chicken,
      infoTip(t('adm.floor.chickenTip'), { label: t('adm.floor.chickenTipAria') }));
    // « Le chat de l’étage »: the same, a grey and white cat.
    const cat = switchToggle(t('adm.floor.cat'), { checked: f?.cat === true });
    const fCat = h('div', { class: 'adm-switch-tip' }, cat,
      infoTip(t('adm.floor.catTip'), { label: t('adm.floor.catTipAria') }));
    const conns = connectorChecklist(f?.connectors || []);
    // « Données de l’étage »: its own, or those of another floor (its host). A floor whose data
    // other floors use keeps its own (one level only, as the server checks): a note instead.
    const floors = office().floors;
    const guests = isNew ? [] : dataGuestsOf(floors, f.id, { stored: true });
    const dataSel = guests.length ? null : select(dataFloorChoices(floors, f), { value: f?.dataFloor || '', name: 'dataFloor' });
    const fData = dataSel
      ? field(t('adm.floor.data'), dataSel, { hint: t('adm.floor.dataHint') })
      : h('div', { class: 'field adm-floor-datahost' }, h('span', { class: 'field-label' }, t('adm.floor.data')),
        h('p', { class: 'adm-note' }, t('adm.floor.dataHost', { n: guests.length, list: guests.map((g) => `« ${g.name} »`).join(', ') })));
    const nameOf = (id) => floors.find((x) => x.id === id)?.name || id;
    openForm({
      title: isNew ? t('adm.floor.createTitle') : t('adm.floor.editTitle', { name: f.name }),
      submitLabel: isNew ? t('adm.floor.create') : t('common.save'),
      fields: [fName, fDesc, color, fCtx, fData, studio, fChicken, fCat, conns.el],
      onSubmit: async ({ err }) => {
        const res = validateFloor({
          name: nameInput.value, description: descInput.value, color: color.value, context: ctxArea.input.value, connectors: conns.getValue(),
          studio: studio.input.checked, chicken: chicken.input.checked, cat: cat.input.checked,
          dataFloor: dataSel ? dataSel.value : f?.dataFloor,
        });
        fName.setError(res.errors.name || null);
        fDesc.setError(res.errors.description || null);
        color.setError(res.errors.color || null);
        fCtx.setError(res.errors.context || null);
        fData.setError?.(res.errors.dataFloor || null);
        if (!res.ok) { err.set(t('adm.formError')); return false; }
        if (isNew) {
          const out = await ctx.api.post('/api/floors', res.value);
          const floor = unwrapItem(out, 'floor');
          if (floor) setOffice(officeWith(office(), 'floors', { level: 'admin', ...floor }));
          ctx.toast.success(t('adm.floor.created', { name: res.value.name }));
        } else {
          const patch = changedFields(f, res.value);
          if (!Object.keys(patch).length) return true;
          // A floor that stops using another floor's data: its teams see other tables and notes.
          const hostId = dataHostOf(office().floors, f.id);
          if ('dataFloor' in patch && hostId !== f.id) {
            const cleared = patch.dataFloor === null;
            const vars = { name: f.name, host: nameOf(hostId), next: nameOf(patch.dataFloor) };
            const ok = await ctx.overlay.confirm({
              title: t(cleared ? 'adm.floor.dataClearTitle' : 'adm.floor.dataSwitchTitle', vars),
              message: t(cleared ? 'adm.floor.dataClearText' : 'adm.floor.dataSwitchText', vars),
              confirmLabel: t(cleared ? 'adm.floor.dataClearOk' : 'adm.floor.dataSwitchOk'),
            });
            if (!ok) return false;
          }
          const out = await ctx.api.patch(`/api/floors/${enc(f.id)}`, patch);
          setOffice(officeWith(office(), 'floors', unwrapItem(out, 'floor') || { id: f.id, ...patch }));
          ctx.toast.success(t('adm.saved'));
        }
        afterChange();
        return true;
      },
    });
  }

  function openDeptForm(f, d) {
    const isNew = !d;
    const nameInput = input({ name: 'name', value: d?.name || '', maxLength: LIMITS.deptName, placeholder: t('adm.dept.namePlaceholder'), autofocus: true, autocomplete: 'off' });
    const fName = field(t('adm.dept.name'), nameInput);
    const color = colorPicker({ value: d?.color || freeColor(floorDepartments(office().departments, f.id)), label: t('adm.dept.color') });
    const ctxArea = countedTextarea({ value: d?.context || '', max: LIMITS.deptContext, rows: 6, name: 'context' });
    const fCtx = field(t('adm.dept.context'), ctxArea, { hint: t('adm.dept.contextHint'), optional: true });
    let leadSel = null;
    let fLead = null;
    if (!isNew) {
      const agents = deptAgents(office().agents, d.id);
      const lead = deptLead(office(), d);
      if (agents.length) {
        leadSel = select(agents.map((a) => ({ value: a.id, label: `${a.name}${a.role ? ` — ${a.role}` : ''}` })), { value: lead?.id || agents[0].id, name: 'lead' });
        fLead = field(t('adm.dept.leadField'), leadSel, { hint: t('adm.dept.leadHint') });
      } else {
        fLead = h('div', { class: 'field' }, h('span', { class: 'field-label' }, t('adm.dept.leadField')), h('p', { class: 'adm-note' }, t('adm.dept.leadNone')));
      }
    }
    openForm({
      title: isNew ? t('adm.dept.createTitle') : t('adm.dept.editTitle', { name: d.name }),
      subtitle: isNew ? t('adm.dept.createSubtitle', { floor: f.name }) : undefined,
      submitLabel: isNew ? t('adm.dept.create') : t('common.save'),
      fields: [fName, color, fCtx, fLead],
      onSubmit: async ({ err }) => {
        const res = validateDept({ name: nameInput.value, color: color.value, context: ctxArea.input.value, leadAgentId: leadSel?.value });
        fName.setError(res.errors.name || null);
        color.setError(res.errors.color || null);
        fCtx.setError(res.errors.context || null);
        if (!res.ok) { err.set(t('adm.formError')); return false; }
        if (isNew) {
          const out = await ctx.api.post(`/api/floors/${enc(f.id)}/departments`, res.value);
          const dept = unwrapItem(out, 'department', 'dept');
          if (dept) setOffice(officeWith(office(), 'departments', { level: 'admin', floorId: f.id, ...dept }));
          ctx.toast.success(t('adm.dept.created', { name: res.value.name }));
        } else {
          const lead = deptLead(office(), d);
          const patch = changedFields({ ...d, leadAgentId: lead?.id || d.leadAgentId }, res.value);
          if (!Object.keys(patch).length) return true;
          const out = await ctx.api.patch(`/api/departments/${enc(d.id)}`, patch);
          setOffice(officeWith(office(), 'departments', unwrapItem(out, 'department', 'dept') || { id: d.id, ...patch }));
          ctx.toast.success(t('adm.saved'));
        }
        afterChange();
        return true;
      },
    });
  }

  /**
   * « Changer d’étage »: the whole department to another floor (POST /api/departments/:id/move).
   * A dry run first: what blocks the move stays in the form (every reason), else its summary is
   * confirmed, then the move itself (a refusal at that point, e.g. a task started meanwhile, is
   * shown the same way). « Changer de données » appears only when the new floor does not use the
   * same data (« Données de l’étage »): the server refuses that change unless it is accepted.
   * « Garder les accès sur tout l’étage » (on by default) unticked: `extendFloorGrants: false`,
   * for a move back (a floor-wide grant on the floor it leaves gives nothing on the new one).
   */
  function openMoveDept(f, d) {
    const targets = moveTargets(office(), d);
    if (!targets.length) return null;
    const floorSel = select(targets.map((x) => ({ value: x.id, label: x.name })), { value: targets[0].id, name: 'floorId' });
    const fFloor = field(t('adm.move.floor'), floorSel);
    const dataSwitch = switchToggle(t('adm.move.allowData'), { hint: t('adm.move.allowDataHint') });
    dataSwitch.classList.add('adm-move-data');
    const grantSwitch = switchToggle(t('adm.move.extendGrants'), { checked: true, hint: t('adm.move.extendGrantsHint', { floor: f.name }) });
    grantSwitch.classList.add('adm-move-grants');
    // What blocks the move: every reason the server gives, one per line.
    const reasons = h('div', { class: 'adm-alert is-amber adm-move-reasons', role: 'alert', hidden: true });
    const showReasons = (list) => {
      const items = (Array.isArray(list) ? list : []).map(String).filter(Boolean);
      reasons.replaceChildren(...(items.length
        ? [icon('alert', { size: 16 }), h('div', {}, h('p', { class: 'adm-move-reasons-title' }, t('adm.move.blocked')),
          h('ul', { class: 'adm-move-reasons-list' }, items.map((r) => h('li', {}, r))))]
        : []));
      reasons.hidden = !items.length;
    };
    const syncData = () => {
      const same = sameDataFloors(office().floors, d.floorId, floorSel.value);
      dataSwitch.hidden = same;
      if (same) dataSwitch.input.checked = false;
    };
    floorSel.addEventListener('change', () => { showReasons([]); syncData(); });
    syncData();
    const path = `/api/departments/${enc(d.id)}/move`;
    return openForm({
      title: t('adm.move.title', { name: d.name }), subtitle: t('adm.move.subtitle', { floor: f.name }),
      submitLabel: t('adm.move.check'),
      fields: [h('p', { class: 'adm-note' }, t('adm.move.text')), fFloor, dataSwitch, grantSwitch, reasons],
      onSubmit: async ({ err }) => {
        showReasons([]);
        const to = targets.find((x) => x.id === floorSel.value) || targets[0];
        const req = {
          floorId: to.id,
          ...(dataSwitch.hidden ? {} : { allowDataChange: dataSwitch.input.checked }),
          ...(grantSwitch.input.checked ? {} : { extendFloorGrants: false }),
        };
        const plan = await ctx.api.post(path, { ...req, dryRun: true });
        if (plan?.unchanged) { err.set(t('adm.move.unchanged')); return false; }
        if (Array.isArray(plan?.blockers) && plan.blockers.length) { showReasons(plan.blockers); return false; }
        const ok = await ctx.overlay.confirm({
          title: t('adm.move.confirmTitle', { dept: d.name, floor: to.name }), message: moveSummary(plan), confirmLabel: t('adm.move.ok'),
        });
        if (!ok) return false;
        try {
          await ctx.api.post(path, req);
        } catch (ex) {
          if (ex?.status === 409 && Array.isArray(ex.details?.reasons) && ex.details.reasons.length) {
            showReasons(ex.details.reasons);
            return false;
          }
          throw ex;
        }
        setOffice(officeAfterMove(office(), d.id, to.id));
        ctx.toast.success(t('adm.move.done', { dept: d.name, floor: to.name }));
        afterChange();
        return true;
      },
    });
  }

  /** Reseed the organisation from config/seed/office.json (POST /api/office/reseed, name typed to confirm). */
  function openReseed() {
    const name = ctx.store.get().office?.name || '';
    const confirmInput = input({ value: '', placeholder: name, autocomplete: 'off', autofocus: true });
    const fConfirm = field(t('adm.reseed.confirmLabel'), confirmInput, { hint: t('adm.reseed.confirmHint', { name }) });
    openForm({
      title: t('adm.reseed.title'), submitLabel: t('adm.reseed.ok'), danger: true,
      fields: [h('p', { class: 'adm-alert is-amber', role: 'note' }, icon('alert', { size: 16 }), h('span', {}, t('adm.reseed.text'))), fConfirm],
      onSubmit: async ({ err }) => {
        if (confirmInput.value.trim() !== name.trim() || !name) { fConfirm.setError(t('adm.reseed.mismatch')); err.set(t('adm.reseed.mismatch')); return false; }
        const out = await ctx.api.post('/api/office/reseed', { confirm: confirmInput.value.trim() }, { timeoutMs: 60_000 });
        ctx.toast.success(t('adm.reseed.done', { ...(out?.counts || {}), backup: out?.backup || '' }));
        afterChange();
        return true;
      },
    });
  }

  function openAgentForm(f, d) {
    const nameInput = input({
      name: 'name', maxLength: LIMITS.agentName, autofocus: true, autocomplete: 'off', class: 'adm-upper',
      onBlur: () => { nameInput.value = nameInput.value.toUpperCase(); },
    });
    const fName = field(t('adm.agent.name'), nameInput, { hint: t('adm.agent.nameHint') });
    const roleInput = input({ name: 'role', maxLength: LIMITS.agentRole, placeholder: t('adm.agent.rolePlaceholder'), autocomplete: 'off' });
    const fRole = field(t('adm.agent.role'), roleInput);
    const doesArea = countedTextarea({ max: LIMITS.agentDoes, rows: 3, name: 'does' });
    const fDoes = field(t('adm.agent.does'), doesArea, { hint: t('adm.agent.doesHint'), optional: true });
    const engineSel = select(AGENT_ENGINES.map((e) => ({ value: e, label: t(`adm.agent.engine.${e}`) })), { value: 'claude', name: 'engine' });
    const fEngine = field(t('adm.agent.engine'), engineSel);
    openForm({
      title: t('adm.agent.createTitle'), subtitle: t('adm.agent.createSubtitle', { dept: d.name, floor: f.name }),
      submitLabel: t('adm.agent.create'),
      fields: [fName, fRole, fDoes, fEngine],
      onSubmit: async ({ err }) => {
        const res = validateAgent({ name: nameInput.value, role: roleInput.value, does: doesArea.input.value, engine: engineSel.value });
        fName.setError(res.errors.name || null);
        fRole.setError(res.errors.role || null);
        fDoes.setError(res.errors.does || null);
        if (!res.ok) { err.set(t('adm.formError')); return false; }
        const out = await ctx.api.post('/api/agents', { floorId: f.id, deptId: d.id, ...res.value });
        const agent = unwrapItem(out, 'agent');
        if (agent) setOffice(officeWith(office(), 'agents', { level: 'admin', floorId: f.id, deptId: d.id, ...agent }));
        expanded.add(d.id);
        render();
        ctx.toast.success(t('adm.agent.created', { name: res.value.name }), agent ? {
          action: { label: t('adm.agent.openSettings'), onClick: () => ctx.navigate(`#/agent/${enc(agent.id)}/settings`) },
        } : undefined);
        afterChange();
        return true;
      },
    });
  }

  const offs = [
    ctx.store.on('office', () => render()),
    ctx.events.on('connectors', () => loadConnectors()),
  ];
  render();
  loadConnectors();
  return {
    el,
    reload: loadConnectors,
    openFloorForm,
    openMoveDept,
    destroy() { destroyed = true; for (const off of offs) off(); },
  };
}
